import _                                                    from 'underscore';
import React, { FC }                                        from 'react';
import { StyledContainer, StyledContainerProps }            from '../../containers';
import { getCSSVar, hexToRGBa }                             from '../../../util';
import { CorvalUIColor, NumberRange1to100, SemanticUISize } from '../../../types';

//@ts-ignore
import lightImgPath from '../../../_assets/images/branding/corval-hex-pattern-tile-light.svg';
//@ts-ignore
import darkImgPath  from '../../../_assets/images/branding/corval-hex-pattern-tile-dark.svg';

export enum HexContainerPreset {
	DARK_BLUE = 'DARK_BLUE',
	WHITE     = 'WHITE',
}

export interface CorvalStylizedHexContainerProps extends StyledContainerProps {
	
	// Theme presets as shorthand for other props
	preset?: HexContainerPreset,
	
	// What size of pattern to display
	size?: SemanticUISize,
	
	// Color inversion flag
	inverted?: boolean,
	
	// The direction the gradient will go to
	fadeDirection?: 'up' | 'down' | 'left' | 'right',
	
	// Directional percentage of fade as a number from 1 - 100, (eg 10 = 0% -> 10% gradient),
	fadeEnd?: NumberRange1to100
	
	// A hex color string
	fadeColor?: string,
	
	[ additionalProps: string ]: any
}

const defaultProps: CorvalStylizedHexContainerProps = {
	preset:        null,
	size:          SemanticUISize.SMALL,
	inverted:      false,
	fadeColor:     null,
	fadeDirection: 'up',
	fadeEnd:       100
};

const presets = {
	[ HexContainerPreset.DARK_BLUE ]: {
		inverted:  true,
		fadeColor: getCSSVar( '--color-darkBlue' ),
		bgColor:   CorvalUIColor.DARKBLUE
	},
	[ HexContainerPreset.WHITE ]:     {
		inverted:  false,
		fadeColor: getCSSVar( '--color-white' ),
		bgColor:   CorvalUIColor.WHITE
	}
};

const getScaleSize = ( scale: number ): string => `${ Math.fround( scale * 175 ) }px`;

const CorvalStylizedHexContainer: FC<CorvalStylizedHexContainerProps> = ( _props ) => {
	
	let stylizedProps = _props;
	
	if( _props.preset ){
		stylizedProps = { ..._props, ...presets[ _props.preset ] } as CorvalStylizedHexContainerProps;
	}
	
	let { preset, size, inverted, fadeDirection, fadeColor, ...props } = stylizedProps;
	
	/* background size = 175 x 202 */
	let backgroundSizes = new Map( [
		[ 'mini', getScaleSize( 0.33 ) ],
		[ 'tiny', getScaleSize( 0.5 ) ],
		[ 'small', getScaleSize( 0.8 ) ],
		[ 'medium', 'auto' ],
		[ 'large', getScaleSize( 1.2 ) ],
		[ 'huge', getScaleSize( 1.5 ) ],
		[ 'massive', getScaleSize( 2.2 ) ],
	] );
	
	let fadeDirections = new Map( [
		[ 'up', 'to bottom' ],
		[ 'down', 'to top' ],
		[ 'left', 'to right' ],
		[ 'right', 'to left' ],
	] );
	
	let fadeDirectionValue = fadeDirections.has( fadeDirection )
	                         ? fadeDirections.get( fadeDirection )
	                         : fadeDirections.get( 'up' );
	
	return (
		<StyledContainer { ..._.omit( props, _.keys( defaultProps ) ) } style={ {
			backgroundImage:    `url("${ !inverted ? darkImgPath : lightImgPath }")`,
			backgroundRepeat:   'repeat',
			backgroundSize:     backgroundSizes.has( size )
			                    ? backgroundSizes.get( size )
			                    : backgroundSizes.get( 'medium' ),
			backgroundPosition: 'center center',
			overflowY:          'auto'
		} }>
			{ !fadeColor ? props.children : (
				
				<>
					<div className={ 'container--fill-area' }
					     style={ {
						     backgroundImage: `linear-gradient( ${ fadeDirectionValue }, ${ fadeColor }, ${ hexToRGBa( fadeColor, 0 ) } ${ props.fadeEnd }%)`,
					     } }/>
					
					{ props.children }
				</>
			) }
		</StyledContainer>
	)
};

CorvalStylizedHexContainer.defaultProps = defaultProps;

export { CorvalStylizedHexContainer }
